import { useQuery } from '@apollo/client';
import { Search as SearchIcon, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PRICE_RULE_SUBJECT_SEARCH_QUERY } from './pricing.gql';

import { SELLABLE_KIND_FROM_GQL } from '@/shared/graphql/enums';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';

export type PriceRuleSubjectKind = 'product' | 'service' | 'kit';

export interface SubjectFilterValue {
  kind: PriceRuleSubjectKind;
  id: string;
  label: string;
}

interface SubjectHit {
  kind: string;
  id: string;
  code: string;
  name: string;
}

interface SubjectSearchResponse {
  searchSellable: SubjectHit[];
}

interface PriceRuleSubjectFilterProps {
  value: SubjectFilterValue | null;
  onChange: (value: SubjectFilterValue | null) => void;
}

const MIN_TERM_LENGTH = 2;

export function PriceRuleSubjectFilter({
  value,
  onChange,
}: PriceRuleSubjectFilterProps): JSX.Element {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(term.trim()), 200);
    return () => window.clearTimeout(handle);
  }, [term]);

  const has_min_term = debounced.length >= MIN_TERM_LENGTH;

  const { data, loading } = useQuery<SubjectSearchResponse>(PRICE_RULE_SUBJECT_SEARCH_QUERY, {
    variables: { term: debounced },
    skip: !has_min_term,
    fetchPolicy: 'cache-and-network',
  });

  const results = data?.searchSellable ?? [];

  if (value) {
    return (
      <div className="flex items-center gap-1.5 rounded-md border border-input bg-muted/40 px-2 py-1.5 text-sm">
        <Badge variant="outline">{t(`pricing.kind_${value.kind}`)}</Badge>
        <span className="max-w-[16rem] truncate">{value.label}</span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0"
          onClick={() => onChange(null)}
          aria-label={t('pricing.subject_filter_clear')}
        >
          <X className="h-3 w-3" aria-hidden />
        </Button>
      </div>
    );
  }

  return (
    <div className="relative w-64">
      <div className="relative">
        <SearchIcon
          className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          type="search"
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          placeholder={t('pricing.subject_filter_placeholder')}
          className="h-9 pl-7 text-sm"
        />
      </div>
      {open && has_min_term ? (
        <ul className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-input bg-background shadow-md">
          {loading && results.length === 0 ? (
            <li className="px-3 py-2 text-xs text-muted-foreground">{t('common.loading')}</li>
          ) : null}
          {results.map((hit) => {
            const kind: PriceRuleSubjectKind =
              SELLABLE_KIND_FROM_GQL[hit.kind as keyof typeof SELLABLE_KIND_FROM_GQL] ?? 'product';
            return (
              <li key={`${kind}:${hit.id}`}>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onChange({ kind, id: hit.id, label: `${hit.code} — ${hit.name}` });
                    setTerm('');
                    setOpen(false);
                  }}
                >
                  <Badge variant="outline">{t(`pricing.kind_${kind}`)}</Badge>
                  <span className="truncate">
                    {hit.code} — {hit.name}
                  </span>
                </button>
              </li>
            );
          })}
          {!loading && results.length === 0 ? (
            <li className="px-3 py-2 text-xs text-muted-foreground">
              {t('pricing.subject_filter_no_results')}
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
